/**
 * WhatsApp Page Component
 * 
 * This component displays the WhatsApp management interface
 * including sessions, bot status, and conversation management.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { useTranslations } from '../context/TranslationsContext';
import { whatsappAPI } from '../api';
import WhatsAppSessions from '../components/whatsapp/WhatsAppSessions';
import WhatsAppStatus from '../components/whatsapp/WhatsAppStatus';
import ConversationHistory from '../components/whatsapp/ConversationHistory';
import WhatsAppConversations from '../components/whatsapp/WhatsAppConversations';
import SearchBar from '../components/common/SearchBar';
import Pagination from '../components/common/Pagination';

const WhatsAppPage = () => {
  const { state, actions } = useApp();
  const { translations } = useTranslations();
  const t = translations[state.currentLanguage];

  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('sessions');
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchWhatsAppData = async () => {
      if (!state.token) return;
      
      actions.setLoading(true);
      try {
        const [sessions, status] = await Promise.all([
          whatsappAPI.getSessions(state.token),
          whatsappAPI.getStatus(state.token)
        ]);
        
        actions.setWhatsappSessions(sessions);
        setWhatsappStatus(status);
      } catch (error) {
        console.error('Error fetching WhatsApp data:', error);
      } finally {
        actions.setLoading(false);
      }
    };

    fetchWhatsAppData();
  }, [state.token]);

  // Filter sessions based on search term
  const filteredSessions = useMemo(() => {
    if (!searchTerm) return state.whatsappSessions;
    
    return state.whatsappSessions.filter(session => 
      session.phone_number?.includes(searchTerm) ||
      session.step?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [state.whatsappSessions, searchTerm]);

  // Paginate filtered sessions
  const paginatedSessions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredSessions.slice(startIndex, endIndex);
  }, [filteredSessions, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredSessions.length / itemsPerPage);

  const handleSearchChange = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };


  const handleSessionSelect = (sessionId) => {
    setSelectedSessionId(sessionId);
    setActiveTab('conversations');
  };

  if (state.loading) {
    return <div className="loading">{t.loading}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h2 className="section-title">{t.whatsapp}</h2>
        <div className="page-actions">
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            placeholder={t.searchWhatsappSessions}
            className="w-64"
          />
        </div>
      </div>

      <WhatsAppStatus status={whatsappStatus} />

      <div className="tabs-container">
        <div className="tabs-header">
          <button
            className={`tab-button ${activeTab === 'sessions' ? 'active' : ''}`}
            onClick={() => setActiveTab('sessions')}
          >
            {t.activeSessions}
          </button>
          <button
            className={`tab-button ${activeTab === 'conversations' ? 'active' : ''}`}
            onClick={() => setActiveTab('conversations')}
          >
            {t.conversationLog}
          </button>
          <button
            className={`tab-button ${activeTab === 'whatsapp-chat' ? 'active' : ''}`}
            onClick={() => setActiveTab('whatsapp-chat')}
          >
            {t.whatsappConversations}
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'sessions' && (
            <div className="bg-white shadow rounded-lg">
              <WhatsAppSessions 
                sessions={paginatedSessions} 
                onSessionSelect={handleSessionSelect}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                totalItems={filteredSessions.length}
                itemsPerPage={itemsPerPage}
              />
            </div>
          )}

          {activeTab === 'conversations' && (
            <div className="bg-white shadow rounded-lg">
              <ConversationHistory 
                selectedSessionId={selectedSessionId}
                selectedPhoneNumber={searchTerm}
              />
            </div>
          )}

          {activeTab === 'whatsapp-chat' && (
            <WhatsAppConversations />
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppPage;
